import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  effect,
  output,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SidebarService } from '../../../core/services/sidebar.service';
import { PermissaoService } from '../../../core/services/permissao.service';
import { PERMISSOES } from '../../../core/constants/permissoes.constants';
import { AuthService } from '../../../core/services/auth.service';
import { ProfileService } from '../../../core/services/profile.service';
import { LayoutService } from '../../../core/services/layout.service';
import { version } from '../../../../../package.json';

interface MenuItem {
  label: string;
  icon: string;
  link: string;
  permissoes: string[];
}

interface MenuSection {
  titulo: string;
  items: MenuItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.collapsed]': 'isCollapsed() && !isMobileOrTablet',
    '[class.mobile-open]': 'isOpenMobile() && isMobileOrTablet',
  },
})
export class SidebarComponent {
  private sidebarService = inject(SidebarService);
  private authService = inject(AuthService);
  private profileService = inject(ProfileService);
  readonly permissaoService = inject(PermissaoService);
  readonly layoutService = inject(LayoutService);
  readonly usuarioCompleto = this.profileService.currentUser;

  readonly nomeExibicaoPerfil = computed(() => {
    const usuario = this.usuarioCompleto();
    if (!usuario) {
      return 'Nome não disponível';
    }
    return usuario.nome || 'Nome não disponível';
  });

  readonly descricaoExibicaoPerfil = computed(() => {
    const usuario = this.usuarioCompleto();
    if (!usuario) {
      return 'Perfil';
    }
    if (usuario.administrador) {
      return 'Administrador';
    }
    return 'Usuário';
  });

  isCollapsed = this.sidebarService.getCollapsed();
  isOpenMobile = this.sidebarService.getIsOpenMobile();
  appVersion = 'v' + version;

  toggleProfileModalEvent = output<void>();

  get isMobileOrTablet() {
    return this.layoutService.isMobile() || this.layoutService.isTablet();
  }

  constructor() {
    // Reset mobile state when transitioning to desktop
    effect(() => {
      if (!this.isMobileOrTablet) {
        this.sidebarService.closeMobile();
      }
    });
  }

  private readonly menuConfig: MenuSection[] = [
    {
      titulo: 'Principal',
      items: [
        {
          label: 'Minhas Aplicações',
          icon: 'bi-grid-1x2',
          link: '/aplicacoes',
          permissoes: [],
        },
      ]
    },
    {
      titulo: 'Administrativo',
      items: [
        {
          label: 'Usuários',
          icon: 'bi-person-badge',
          link: '/usuarios',
          permissoes: [PERMISSOES.SGI.USUARIOS.LISTAR],
        },
      ]
    }
  ];

  menuSections = computed<MenuSection[]>(() => {
    this.permissaoService.carregado();

    return this.menuConfig
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          this.permissaoService.hasAnyPermission(item.permissoes)
        ),
      }))
      .filter((section) => section.items.length > 0);
  });

  get isExpanded() {
    return this.sidebarService.isExpanded();
  }

  toggleSidebar() {
    this.sidebarService.toggle();
  }

  toggleProfileModal() {
    this.toggleProfileModalEvent.emit();
  }

  logout() {
    this.authService.logout();
  }
}